/**
 * Plan comptable OHADA SYSCOHADA révisé — référentiel 2026 + comptes opérationnels Nexxus.
 * Source officielle : REFERENCE_COMPTA_SYSCOHADA_2026.xlsx (classes 1 à 9).
 * Les comptes à 6 chiffres (paie) et les alias TVA 4431/4451/4452 restent pour l’existant.
 */
import type { CompteOhada } from '@shared/types/CompteOhada';
import { PLAN_SYSCOHADA_2026 } from '@shared/constants/PlanComptableSyscohada2026';

/** Comptes Nexxus non listés tels quels dans le PCG (sous-comptes paie, alias TVA, historiques immo). */
const EXTENSIONS_OPERATIONNELLES: Record<string, CompteOhada> = {
  '244': {
    code: '244',
    libelle: 'Matériel et outillage (alias opérationnel 215)',
    classe: 2,
    type: 'actif',
  },
  '282': {
    code: '282',
    libelle: 'Amortissements des terrains aménagés (alias 281)',
    classe: 2,
    type: 'passif',
  },
  '283': {
    code: '283',
    libelle: 'Amortissements des bâtiments (alias 281)',
    classe: 2,
    type: 'passif',
  },
  '2818': {
    code: '2818',
    libelle: 'Amortissements des autres immo. corporelles',
    classe: 2,
    type: 'passif',
  },
  '2844': {
    code: '2844',
    libelle: 'Amortissements du matériel (alias 281)',
    classe: 2,
    type: 'passif',
  },
  '2845': {
    code: '2845',
    libelle: 'Amortissements du matériel de transport (alias 281)',
    classe: 2,
    type: 'passif',
  },
  /** SYSC-02 — racine 421 = avances et acomptes (pas les rémunérations dues). */
  '421000': {
    code: '421000',
    libelle: 'Personnel — Avances et acomptes (moteur paie)',
    classe: 4,
    type: 'actif',
  },
  /** SYSC-02 — racine 422 = rémunérations dues (net à payer). */
  '422000': {
    code: '422000',
    libelle: 'Personnel — Rémunérations dues (moteur paie)',
    classe: 4,
    type: 'passif',
  },
  /** @deprecated Alias historique — préférer 421000 (avances). Racine 425 = dépôts. */
  '425000': {
    code: '425000',
    libelle: 'Personnel — Dépôts (alias historique avances, préférer 421000)',
    classe: 4,
    type: 'actif',
  },
  '4313': { code: '4313', libelle: 'CNSS', classe: 4, type: 'passif' },
  '4314': { code: '4314', libelle: 'CNAMGS', classe: 4, type: 'passif' },
  '4421': { code: '4421', libelle: 'IRPP', classe: 4, type: 'passif' },
  '4424': { code: '4424', libelle: 'Précompte IRVM à reverser DGI', classe: 4, type: 'passif' },
  '4427': { code: '4427', libelle: 'Précompte IS à reverser DGI', classe: 4, type: 'passif' },
  /** Alias opérationnel — référentiel 442 État — TVA facturée. */
  '4431': { code: '4431', libelle: 'État — TVA facturée (alias 442)', classe: 4, type: 'passif' },
  /** Alias opérationnel — référentiel 444 État — TVA récupérable (immo). */
  '4451': {
    code: '4451',
    libelle: 'TVA récupérable sur immobilisations (alias 444)',
    classe: 4,
    type: 'actif',
  },
  /** Alias opérationnel — référentiel 444 État — TVA récupérable (biens). */
  '4452': {
    code: '4452',
    libelle: 'TVA récupérable sur biens et services (alias 444)',
    classe: 4,
    type: 'actif',
  },
  /** @deprecated Alias PCG FR — préférer 444 / 4452. */
  '4456': { code: '4456', libelle: 'TVA déductible sur achats (alias 444)', classe: 4, type: 'actif' },
  /** @deprecated Alias PCG FR — préférer 442 / 4431. */
  '4457': { code: '4457', libelle: 'TVA collectée sur ventes (alias 442)', classe: 4, type: 'passif' },
  '4471': { code: '4471', libelle: 'TFP / CFP à payer', classe: 4, type: 'passif' },
  '4472': { code: '4472', libelle: 'CDS à payer (ex-FNH)', classe: 4, type: 'passif' },
  '4473': { code: '4473', libelle: 'TCS', classe: 4, type: 'passif' },
  '4474': { code: '4474', libelle: 'CSS à payer', classe: 4, type: 'passif' },
  '4475': { code: '4475', libelle: 'FGTLS à payer', classe: 4, type: 'passif' },
  '648100': {
    code: '648100',
    libelle: 'Autres charges de personnel (cat3)',
    classe: 6,
    type: 'charge',
  },
  '6594': {
    code: '6594',
    libelle: 'Dotations aux provisions créances douteuses',
    classe: 6,
    type: 'charge',
  },
  '661100': { code: '661100', libelle: 'Salaires de base', classe: 6, type: 'charge' },
  '661200': { code: '661200', libelle: 'Sursalaires', classe: 6, type: 'charge' },
  '661300': { code: '661300', libelle: 'Indemnités et primes', classe: 6, type: 'charge' },
  '661400': { code: '661400', libelle: 'Heures supplémentaires', classe: 6, type: 'charge' },
  '663100': { code: '663100', libelle: 'Cotisations CNSS patronales', classe: 6, type: 'charge' },
  '663200': { code: '663200', libelle: 'Cotisations CNAMGS patronales', classe: 6, type: 'charge' },
  '664100': {
    code: '664100',
    libelle: 'Taxes patronales TFP/CDS/FGTLS',
    classe: 6,
    type: 'charge',
  },
  '6811': {
    code: '6811',
    libelle: 'Dotations aux amortissements des immobilisations incorporelles',
    classe: 6,
    type: 'charge',
  },
  '6813': {
    code: '6813',
    libelle: 'Dotations aux amortissements des immobilisations corporelles',
    classe: 6,
    type: 'charge',
  },
  '6815': {
    code: '6815',
    libelle: 'Dotations aux provisions congés payés',
    classe: 6,
    type: 'charge',
  },
  '6911': {
    code: '6911',
    libelle: 'Dotations aux provisions indemnités fin de carrière',
    classe: 6,
    type: 'charge',
  },
  '7011': { code: '7011', libelle: 'Ventes de marchandises', classe: 7, type: 'produit' },
  '791000': {
    code: '791000',
    libelle: 'Transferts de charges — avantages en nature',
    classe: 7,
    type: 'produit',
  },
};

export const PLAN_COMPTABLE_OHADA: Record<string, CompteOhada> = {
  ...PLAN_SYSCOHADA_2026,
  ...EXTENSIONS_OPERATIONNELLES,
};

/** Plus long préfixe connu (ex. 661100 → lui-même, 661199 → 661). */
export function lookupCompteOhada(code: string): CompteOhada | undefined {
  const trimmed = code.trim();
  if (!trimmed) return undefined;
  if (PLAN_COMPTABLE_OHADA[trimmed]) return PLAN_COMPTABLE_OHADA[trimmed];
  for (let i = trimmed.length - 1; i >= 2; i--) {
    const hit = PLAN_COMPTABLE_OHADA[trimmed.slice(0, i)];
    if (hit) return hit;
  }
  return undefined;
}
